import { z } from 'zod/v3';

/**
 * Pattern information for repetitive structures
 */
export const PatternInfoSchema = z.object({
  type: z.enum(['list', 'table', 'grid', 'menu', 'repeater']),
  containerSelector: z.string(),
  itemIndex: z.number(),
  itemPattern: z.string(),
  identifyBy: z.enum(['index', 'content', 'attribute']),
  contentKey: z.string().optional(),
  attributeKey: z.string().optional(),
  attributeValue: z.string().optional(),
});

export type PatternInfoDTO = z.infer<typeof PatternInfoSchema>;

/**
 * Element fingerprint containing all identifying information
 */
export const ElementFingerprintSchema = z.object({
  tagName: z.string(),
  
  // Element attributes
  attributes: z.object({
    id: z.string().optional(),
    role: z.string().optional(),
    ariaLabel: z.string().optional(),
    dataTestId: z.string().optional(),
    dataAttributes: z.record(z.string()).optional(),
    href: z.string().optional(),
    type: z.string().optional(),
    name: z.string().optional(),
    placeholder: z.string().optional(),
    classList: z.array(z.string()).optional(),
  }).optional(),
  
  // Content information
  content: z.object({
    text: z.string().optional(),
    value: z.string().optional(),
    labelText: z.string().optional(),
  }).optional(),
  
  // Visual features
  visual: z.object({
    boundingBox: z.object({
      x: z.number(),
      y: z.number(),
      width: z.number(),
      height: z.number(),
    }).optional(),
    isVisible: z.boolean(),
    isInViewport: z.boolean(),
    viewportRegion: z.string().optional(),
    elementType: z.string().optional(),
    zIndex: z.string().optional(),
    position: z.string().optional(),
  }).optional(),
  
  // Context information
  context: z.object({
    parentSelector: z.string().optional(),
    nearestLandmark: z.string().optional(),
    indexInParent: z.number(),
    indexAmongSimilar: z.number(),
    inModal: z.boolean().optional(),
    inDropdown: z.boolean().optional(),
    containerRole: z.string().optional(),
    containerClass: z.string().optional(),
    siblingCount: z.number().optional(),
    depth: z.number().optional(),
  }).optional(),
  
  // Interaction characteristics
  interaction: z.object({
    isInteractive: z.boolean(),
    interactionType: z.enum(['click', 'input', 'select', 'hover', 'focus']).optional(),
    hasClickHandler: z.boolean(),
    isFormElement: z.boolean(),
    isFocusable: z.boolean(),
    actualTargetSelector: z.string().optional(),
  }).optional(),
  
  // Element states information
  elementStates: z.object({
    default: z.object({
      classes: z.array(z.string()),
      attributes: z.record(z.string()),
      styles: z.object({
        display: z.string().optional(),
        visibility: z.string().optional(),
        opacity: z.string().optional(),
      }).optional(),
    }).optional(),
    hover: z.object({
      classes: z.array(z.string()),
      attributes: z.record(z.string()),
      styles: z.object({
        display: z.string().optional(),
        visibility: z.string().optional(),
        opacity: z.string().optional(),
      }).optional(),
    }).optional(),
    focus: z.object({
      classes: z.array(z.string()),
      attributes: z.record(z.string()),
      styles: z.object({
        display: z.string().optional(),
        visibility: z.string().optional(),
        opacity: z.string().optional(),
      }).optional(),
    }).optional(),
    active: z.object({
      classes: z.array(z.string()),
      attributes: z.record(z.string()),
      styles: z.object({
        display: z.string().optional(),
        visibility: z.string().optional(),
        opacity: z.string().optional(),
      }).optional(),
    }).optional(),
  }).optional(),
  
  // State when interaction occurred
  interactionState: z.enum(['default', 'hover', 'focus', 'active']).optional(),
  
  // State transitions information
  stateTransitions: z.array(z.object({
    from: z.enum(['default', 'hover', 'focus', 'active']),
    to: z.enum(['default', 'hover', 'focus', 'active']),
    trigger: z.enum(['mouseenter', 'mouseleave', 'focus', 'blur', 'click']),
    changes: z.object({
      classesAdded: z.array(z.string()).optional(),
      classesRemoved: z.array(z.string()).optional(),
      attributesChanged: z.record(z.string()).optional(),
      stylesChanged: z.record(z.string()).optional(),
    }),
  })).optional(),
});

export type ElementFingerprintDTO = z.infer<typeof ElementFingerprintSchema>;

/**
 * Single selector strategy with element fingerprint
 */
export const SelectorStrategySchema = z.object({
  // Core selectors
  primary: z.string(),
  structural: z.string(),
  semantic: z.string(),
  contextual: z.string(),
  
  // Additional selector dimensions
  byId: z.string().optional(),
  byTestId: z.string().optional(),
  byContent: z.string().optional(),
  byAttributes: z.string().optional(),
  fallbacks: z.array(z.string()).optional(),
  
  // Pattern and XPath
  pattern: PatternInfoSchema.optional(),
  xpath: z.string().optional(),
  
  // Element fingerprint for validation and fallback
  fingerprint: ElementFingerprintSchema.optional(),
  
  // Metadata
  confidence: z.number(),
  reliability: z.enum(['high', 'medium', 'low']).optional(),
});

export type SelectorStrategyDTO = z.infer<typeof SelectorStrategySchema>;

/**
 * RFC-109: Healed selector from Layer 3 success
 * Contains XPath + fresh fingerprint for precise element targeting
 *
 * RFC-109 v2: Added preAction, methodOverride for complex scenarios
 */
export const HealedSelectorSchema = z.object({
  // === Precise positioning ===
  xpath: z.string(),                    // XPath from Stagehand (most precise)
  cssSelector: z.string().optional(),   // CSS selector as fallback

  // === Validation info (fresh fingerprint) ===
  fingerprint: ElementFingerprintSchema, // Captured from successful element

  // === Metadata ===
  method: z.enum(['observe', 'llm']),   // Discovery method: Layer 1.5 observe or Layer 3 LLM
  confidence: z.number(),               // 0-100
  healedAt: z.string(),                 // ISO timestamp
  originalSelector: z.string().optional(), // Original failed selector

  // === Context (optional) ===
  actionDescription: z.string().optional(), // LLM's element description
  pageUrl: z.string().optional(),           // Page URL when discovered

  // === RFC-109 v2: Pre-action for hover-revealed elements ===
  preAction: z.object({
    xpath: z.string(),                  // XPath of element to hover/click first
    method: z.enum(['hover', 'click']), // Action to perform before main action
    description: z.string().optional(), // LLM's description of pre-action target
  }).optional(),

  // === RFC-109 v2: Method override (e.g., click -> doubleClick) ===
  methodOverride: z.enum(['click', 'doubleClick', 'hover']).optional(),
});

export type HealedSelectorDTO = z.infer<typeof HealedSelectorSchema>;

/**
 * Collection of selector strategies for robust element identification
 */
export const SelectorStrategiesSchema = z.object({
  strategies: z.array(SelectorStrategySchema),
  preferredStrategy: z.number().default(0),
  timestamp: z.string(),

  // Page context
  pageContext: z.object({
    url: z.string(),
    framework: z.array(z.string()).optional(),
  }).optional(),

  // RFC-109: Healed selector from Layer 3 success
  healed: HealedSelectorSchema.optional(),

  // RFC-109 v2: Healed count for statistics
  healedCount: z.number().default(0),

  version: z.string().default('2.0'),
});

export type SelectorStrategiesDTO = z.infer<typeof SelectorStrategiesSchema>;